/**
 * Which clock each kind of time-related value is shown on, and whether it carries its zone label.
 *
 * MYM-260 put session times on the center's clock, so a guardian in another zone sees the time they
 * are expected at the center rather than the time on their own phone. Product revisits these calls
 * — where the zone label belongs was still being argued on the ticket — so this table is the only
 * thing that changes when they do. Call sites name the kind of value they are rendering and never
 * pick a zone themselves.
 *
 * Values that are a bare calendar day with no time of day are outside time zone conversion by
 * agreement. `reportDay` is the one that routes through here; student dates of birth, invoices,
 * enrollment periods and enrollment agreements render their written day directly and were left
 * untouched by MYM-260. Add them here if that ever needs to become a policy decision too.
 */

export type TimeZoneSource =
  /** The center's zone, from the logged-in center. */
  | 'center'
  /** Whatever zone the guardian's device is set to. */
  | 'device'
  /** Read as UTC — for a payload whose wall clock is already the one we want to print. */
  | 'utc'
  /** A calendar day with no time of day: rendered as written, never converted. */
  | 'none';

export interface TimeDisplayRule {
  zone: TimeZoneSource;
  /** Whether a rendered time carries its zone abbreviation, e.g. "6:00 PM PDT". */
  zoneLabel: boolean;
}

export const TIME_DISPLAY_POLICY = {
  /** Session cards, history, the booking grid and its timeslots. */
  sessionTime: { zone: 'center', zoneLabel: false },

  /** The card a guardian reads right after booking or rescheduling. */
  bookingConfirmation: { zone: 'center', zoneLabel: true },

  /** The free cancel/reschedule cut-off. A fee depends on it, so it always names its zone. */
  cancellationDeadline: { zone: 'center', zoneLabel: true },

  notification: { zone: 'center', zoneLabel: false },

  /**
   * The session-summary payload stamps the center's wall clock with a `Z` it does not mean: Radius
   * has already converted it upstream, so "2026-09-08T18:00:00.000Z" is 6:00 PM at the center.
   * Reading it as UTC is what prints that. Change this to 'center' once the API returns a true UTC
   * instant — nothing else has to move.
   */
  attendanceTime: { zone: 'utc', zoneLabel: true },

  /** The .ics lands in the guardian's own calendar app, which renders it in the device zone anyway. */
  calendarExport: { zone: 'device', zoneLabel: false },

  /** Report dates, assessment dates, projected completion months. */
  reportDay: { zone: 'none', zoneLabel: false },
} as const satisfies Record<string, TimeDisplayRule>;

export type TimeKind = keyof typeof TIME_DISPLAY_POLICY;
